import type { ModuleStorage } from "@artis/sdk";
import { useCallback, useRef, useState } from "react";

export const DONT_CONFIRM_SHEET_DELETE_KEY = "dontConfirmSheetDelete";

type SheetInfo = { id: string; name: string; cells: Record<string, unknown> };

export function useDeleteSheetConfirm({
  storage,
  sheets,
  deleteSheet,
}: {
  storage: ModuleStorage;
  sheets: SheetInfo[];
  deleteSheet: (id: string) => void;
}) {
  const [pending, setPending] = useState<{ id: string; name: string } | null>(null);
  const [dontAskAgain, setDontAskAgain] = useState(false);

  const latest = useRef({ sheets, deleteSheet });
  latest.current = { sheets, deleteSheet };

  const requestDelete = useCallback(
    (id: string) => {
      const sheet = latest.current.sheets.find((s) => s.id === id);
      if (!sheet || Object.keys(sheet.cells).length === 0) {
        latest.current.deleteSheet(id);
        return;
      }
      void storage.get<boolean>(DONT_CONFIRM_SHEET_DELETE_KEY).then((optedOut) => {
        if (optedOut === true) latest.current.deleteSheet(id);
        else setPending({ id: sheet.id, name: sheet.name });
      });
    },
    [storage],
  );

  const close = useCallback(() => {
    setPending(null);
    setDontAskAgain(false);
  }, []);

  const confirm = useCallback(() => {
    if (!pending) return;
    if (dontAskAgain) void storage.set(DONT_CONFIRM_SHEET_DELETE_KEY, true).catch(() => {});
    latest.current.deleteSheet(pending.id);
    close();
  }, [pending, dontAskAgain, storage, close]);

  return { requestDelete, pending, confirm, cancel: close, dontAskAgain, setDontAskAgain };
}
